/**
 * 모바일 네비게이션 렌더러 - 하단 네비게이션 바 (HeroUI 버전)
 * ui-layout 라이브러리에서 공통으로 사용
 */ import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SidebarRenderer } from './SidebarRenderer';
import { Button, Card, CardBody, Popover, PopoverTrigger, PopoverContent, cn } from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
// Lucide React 직접 import
import { Menu, MessageCircle, Pen as PenLine, User, Plus, LogIn, LogOut } from 'lucide-react';
import * as m from "@paraglide/messages";
import { MAIN_LAYOUT_TEST_IDS, CHAT_TEST_IDS } from '@edusense/shared-constants';
export const MobileNavigationRenderer = ({ navigationItems = [], activeMenu, onMenuClick, onChatSidebarToggle, userName, userAvatar, logoText, logoImage, onLogoClick, isAuthenticated = false, onWriteButtonClick, onLoginClick, onLogout, logoTextStyle, userProfileComponent })=>{
    // 디버깅: props 확인
    console.log('🟡 MobileNavigationRenderer props:');
    console.log('🟡 userName:', userName);
    console.log('🟡 userAvatar:', userAvatar);
    console.log('🟡 isAuthenticated:', isAuthenticated);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
    // 모바일용은 mobileNavBarVisible이 true인 항목만 표시
    const navItems = navigationItems.filter((item)=>item.mobileNavBarVisible);
    // 네비게이션 아이템 ID를 testid로 매핑하는 함수
    const getNavTestId = (itemId)=>{
        switch(itemId){
            case 'home':
                return MAIN_LAYOUT_TEST_IDS.NAV_FEED;
            case 'courses':
                return MAIN_LAYOUT_TEST_IDS.NAV_LEARN;
            case 'about':
                return MAIN_LAYOUT_TEST_IDS.NAV_DISCOVER;
            case 'profile':
                return MAIN_LAYOUT_TEST_IDS.NAV_PROFILE;
            case 'settings':
                return MAIN_LAYOUT_TEST_IDS.NAV_SETTINGS;
            default:
                return `nav-${itemId}`;
        }
    };
    const openSidebar = ()=>{
        setIsSidebarOpen(true);
    };
    const closeSidebar = ()=>{
        setIsSidebarOpen(false);
    };
    const openSidebarFromActionMenu = ()=>{
        console.log('🔴 openSidebarFromActionMenu called');
        // 먼저 액션 메뉴를 닫고
        setIsActionMenuOpen(false);
        // 충분한 지연 후 사이드바 열기
        setTimeout(()=>{
            console.log('🔵 Setting sidebar open, current state:', isSidebarOpen);
            setIsSidebarOpen(true);
        }, 100);
    };
    // 사이드바 상태 변경 감지
    useEffect(()=>{
        console.log('🟢 Sidebar state changed:', isSidebarOpen);
    }, [
        isSidebarOpen
    ]);
    const handleMenuItemClick = (itemId)=>{
        if (onMenuClick) {
            onMenuClick(itemId);
        }
        // 팝오버 메뉴는 닫기 (FAB 버튼 클릭 시)
        setIsActionMenuOpen(false);
    };
    // 사이드바 내부 탭 클릭 시에는 사이드바를 닫지 않음
    const handleSidebarMenuItemClick = (itemId)=>{
        if (onMenuClick) {
            onMenuClick(itemId);
        }
        // 사이드바는 유지하고 팝오버만 닫기
        setIsActionMenuOpen(false);
    };
    const navButtonVariants = {
        inactive: {
            scale: 1
        },
        active: {
            scale: 1.05,
            transition: {
                duration: 0.2,
                type: "spring",
                stiffness: 400,
                damping: 25
            }
        },
        tap: {
            scale: 0.95
        }
    };
    const fabVariants = {
        closed: {
            rotate: 0
        },
        open: {
            rotate: 45,
            transition: {
                duration: 0.2,
                type: "spring",
                stiffness: 200
            }
        }
    };
    const handleLogout = async ()=>{
        if (!onLogout) {
            return;
        }
        closeSidebar();
        setIsActionMenuOpen(false);
        try {
            await onLogout();
        } catch (error) {
            console.error('[MobileNavigationRenderer] Logout failed:', error);
        }
    };
    const NavButton = ({ item })=>{
        const iconNode = /*#__PURE__*/ React.isValidElement(item.icon) ? /*#__PURE__*/ React.cloneElement(item.icon, {
            className: cn('transition-colors duration-200', item.icon.props.className),
            color: undefined
        }) : item.icon;
        return /*#__PURE__*/ React.createElement(motion.div, {
            variants: navButtonVariants,
            initial: "inactive",
            animate: activeMenu === item.id ? "active" : "inactive",
            whileTap: "tap",
            className: "relative"
        }, /*#__PURE__*/ React.createElement(Button, {
            isIconOnly: false,
            variant: "light",
            color: "default",
            size: "sm",
            "data-testid": getNavTestId(item.id),
            onPress: ()=>handleMenuItemClick(item.id),
            className: cn("relative flex-col h-auto px-2 pt-1 pb-3 min-w-0 transition-colors", "bg-transparent hover:bg-transparent", activeMenu === item.id ? "text-primary font-semibold" : "text-default-600 hover:text-default-700")
        }, /*#__PURE__*/ React.createElement(motion.div, {
            animate: activeMenu === item.id ? {
                scale: [
                    1,
                    1.05,
                    1
                ],
                y: [
                    0,
                    -1,
                    0
                ]
            } : {
                scale: 1,
                y: 0
            },
            transition: {
                duration: activeMenu === item.id ? 3 : 0.2,
                repeat: activeMenu === item.id ? Infinity : 0,
                ease: "easeInOut",
                repeatType: "reverse"
            },
            className: "flex items-center justify-center",
            style: {
                color: 'inherit'
            }
        }, iconNode), /*#__PURE__*/ React.createElement(motion.span, {
            className: "text-[10px] mt-1 whitespace-nowrap",
            animate: activeMenu === item.id ? {
                color: "inherit",
                fontWeight: 600
            } : {
                color: "currentColor",
                fontWeight: 500
            },
            transition: {
                duration: 0.2
            }
        }, item.label), activeMenu === item.id && /*#__PURE__*/ React.createElement(motion.div, {
            layoutId: "mobile-nav-underline",
            className: "absolute bottom-0 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full bg-primary",
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 30
            }
        })));
    };
    return /*#__PURE__*/ React.createElement("div", {
        style: {
            position: 'relative',
            width: '100%',
            height: '100%'
        }
    }, /*#__PURE__*/ React.createElement(Card, {
        className: cn("fixed bottom-0 left-0 right-0 rounded-none z-50", "bg-gradient-to-b from-background/98 to-background/95", "backdrop-blur-xl backdrop-saturate-150", "shadow-[0_-12px_32px_rgba(0,0,0,0.08)]", "shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"),
        shadow: "none"
    }, /*#__PURE__*/ React.createElement(CardBody, {
        className: "p-0 overflow-hidden"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "relative"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent"
    }), /*#__PURE__*/ React.createElement("div", {
        className: "absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/10 to-transparent blur-sm"
    }), /*#__PURE__*/ React.createElement("div", {
        className: "flex items-center justify-center h-[60px] px-3"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "flex items-center w-full justify-evenly"
    }, navItems.slice(0, 2).map((item)=>/*#__PURE__*/ React.createElement(NavButton, {
            key: item.id,
            item: item
        })), /*#__PURE__*/ React.createElement(Popover, {
        isOpen: isActionMenuOpen,
        onOpenChange: setIsActionMenuOpen,
        placement: "top",
        showArrow: true
    }, /*#__PURE__*/ React.createElement(PopoverTrigger, null, /*#__PURE__*/ React.createElement(Button, {
        isIconOnly: true,
        color: "primary",
        variant: "shadow",
        size: "lg",
        "data-testid": CHAT_TEST_IDS.ACTION_MENU_TRIGGER,
        onPress: ()=>{
            if (!isAuthenticated) {
                onLoginClick?.();
            }
            setIsActionMenuOpen((prev)=>!prev);
        },
        className: "relative bg-gradient-to-br from-primary-400 to-primary-600 shadow-xl shadow-primary/40"
    }, /*#__PURE__*/ React.createElement(motion.div, {
        variants: fabVariants,
        initial: "closed",
        animate: isActionMenuOpen ? "open" : "closed"
    }, /*#__PURE__*/ React.createElement(Plus, {
        size: 24,
        className: "text-white"
    })))), /*#__PURE__*/ React.createElement(PopoverContent, {
        className: "p-2"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "flex flex-col gap-2 w-40"
    }, /*#__PURE__*/ React.createElement(Button, {
        variant: "light",
        size: "sm",
        color: "default",
        startContent: /*#__PURE__*/ React.createElement(Menu, {
            size: 16
        }),
        onPress: openSidebarFromActionMenu,
        className: "justify-start"
    }, m.menu_open ? m.menu_open() : '메뉴'), isAuthenticated ? /*#__PURE__*/ React.createElement(React.Fragment, null, onChatSidebarToggle && /*#__PURE__*/ React.createElement(Button, {
        variant: "flat",
        size: "sm",
        color: "primary",
        startContent: /*#__PURE__*/ React.createElement(MessageCircle, {
            size: 16
        }),
        onPress: ()=>{
            onChatSidebarToggle();
            setIsActionMenuOpen(false);
        },
        "data-testid": CHAT_TEST_IDS.SIDEBAR_TOGGLE_BUTTON,
        className: "justify-start"
    }, m.common_chat()), onWriteButtonClick && /*#__PURE__*/ React.createElement(Button, {
        variant: "flat",
        size: "sm",
        color: "primary",
        startContent: /*#__PURE__*/ React.createElement(PenLine, {
            size: 16
        }),
        onPress: ()=>{
            onWriteButtonClick();
            setIsActionMenuOpen(false);
        },
        className: "justify-start"
    }, m.common_compose()), onLogout && /*#__PURE__*/ React.createElement(Button, {
        variant: "light",
        size: "sm",
        color: "danger",
        startContent: /*#__PURE__*/ React.createElement(LogOut, {
            size: 16
        }),
        onPress: handleLogout,
        className: "justify-start"
    }, m.common_logout?.() ?? '로그아웃')) : /*#__PURE__*/ React.createElement(Button, {
        variant: "shadow",
        size: "sm",
        color: "primary",
        startContent: /*#__PURE__*/ React.createElement(LogIn, {
            size: 16
        }),
        onPress: ()=>{
            onLoginClick?.();
            setIsActionMenuOpen(false);
        },
        className: "justify-start"
    }, m.common_login())))), navItems[2] && /*#__PURE__*/ React.createElement(NavButton, {
        item: navItems[2]
    }), /*#__PURE__*/ React.createElement(motion.div, {
        variants: navButtonVariants,
        initial: "inactive",
        animate: activeMenu === 'profile' ? "active" : "inactive",
        whileTap: "tap"
    }, /*#__PURE__*/ React.createElement(Button, {
        isIconOnly: false,
        variant: "light",
        color: "default",
        size: "sm",
        "data-testid": getNavTestId('profile'),
        onPress: ()=>handleMenuItemClick('profile'),
        className: cn("relative flex-col h-auto py-1 px-2 min-w-0 transition-colors", "bg-transparent hover:bg-transparent", activeMenu === 'profile' ? "text-primary font-semibold" : "text-default-600 hover:text-default-700")
    }, /*#__PURE__*/ React.createElement(motion.div, {
        animate: activeMenu === 'profile' ? {
            scale: [
                1,
                1.05,
                1
            ],
            y: [
                0,
                -1,
                0
            ]
        } : {
            scale: 1,
            y: 0
        },
        transition: {
            duration: activeMenu === 'profile' ? 3 : 0.2,
            repeat: activeMenu === 'profile' ? Infinity : 0,
            ease: "easeInOut",
            repeatType: "reverse"
        },
        className: "flex items-center justify-center",
        style: {
            color: 'inherit'
        }
    }, /*#__PURE__*/ React.createElement(User, {
        size: 20,
        className: cn("text-current", activeMenu === 'profile' && "text-primary")
    })), /*#__PURE__*/ React.createElement(motion.span, {
        className: "text-[10px] mt-1 whitespace-nowrap",
        animate: activeMenu === 'profile' ? {
            color: "inherit",
            fontWeight: 600
        } : {
            color: "currentColor",
            fontWeight: 500
        },
        transition: {
            duration: 0.2
        }
    }, m.common_profile()), activeMenu === 'profile' && /*#__PURE__*/ React.createElement(motion.div, {
        layoutId: "mobile-nav-underline",
        className: "absolute bottom-0 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full bg-primary",
        transition: {
            type: "spring",
            stiffness: 400,
            damping: 30
        }
    })))))))), /*#__PURE__*/ createPortal(/*#__PURE__*/ React.createElement(AnimatePresence, null, isSidebarOpen && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(motion.div, {
        key: "sidebar-overlay",
        initial: {
            opacity: 0
        },
        animate: {
            opacity: 1
        },
        exit: {
            opacity: 0
        },
        onClick: closeSidebar,
        className: "fixed inset-0 bg-black/50 z-[998] backdrop-blur-sm",
        transition: {
            duration: 0.2,
            ease: "easeInOut"
        }
    }), /*#__PURE__*/ React.createElement(motion.div, {
        key: "sidebar-container",
        initial: {
            x: "-100%"
        },
        animate: {
            x: 0
        },
        exit: {
            x: "-100%"
        },
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 30
        },
        className: "fixed top-0 left-0 bottom-0 w-[280px] bg-background z-[999] shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
    }, /*#__PURE__*/ React.createElement(SidebarRenderer, {
        navigationItems: navigationItems,
        activeMenu: activeMenu,
        onMenuClick: handleSidebarMenuItemClick,
        userName: userName,
        userAvatar: userAvatar,
        logoText: logoText,
        logoImage: logoImage,
        onLogoClick: onLogoClick,
        isMobileSidebar: true,
        onClose: closeSidebar,
        isAuthenticated: isAuthenticated,
        onLoginClick: onLoginClick,
        onLogout: onLogout,
        logoTextStyle: logoTextStyle,
        userProfileComponent: userProfileComponent
    })))), document.body));
};
export default MobileNavigationRenderer;
